/**
 * Centralized domain configuration for DataHub Frontend
 * This file contains all domain and URL configurations
 * Synchronized with Docusaurus domain configuration
 */

// Get environment from React env variables
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Base domains configuration - matching Docusaurus structure
const domains = {
    production: {
        main: 'https://datahub.n2nai.io',
        api: 'https://datahub.n2nai.io/api/v1',
        jupyter: 'https://datahub.n2nai.io/jupyter',
        airflow: 'https://datahub.n2nai.io/airflow',
        docs: 'https://datahub.n2nai.io/docs',
        s3: 'https://datahub.n2nai.io'
    },
    development: {
        main: 'http://localhost:3000',
        api: 'http://localhost:5000/api/v1',
        jupyter: 'http://localhost:5020/jupyter',
        airflow: 'http://localhost:8080',
        docs: 'http://localhost:3001',
        s3: 'http://localhost:9000'
    },
    staging: {
        main: 'https://staging-datahub.n2nai.io',
        api: 'https://staging-datahub.n2nai.io/api/v1',
        jupyter: 'https://staging-datahub.n2nai.io/jupyter',
        airflow: 'https://staging-datahub.n2nai.io/airflow',
        docs: 'https://staging-datahub.n2nai.io/docs',
        s3: 'https://staging-datahub.n2nai.io'
    }
};

// Service ports (for development reference)
const servicePorts = {
    frontend: 3000,
    docusaurus: 3001,
    backend: 5000,
    jupyterhub: 5020,
    jupyterhubAPI: 5022,
    airflow: 8080,
    mongodb: 27017,
    postgresql: 5432,
    redis: 6379,
    minio: 9000
};

// Determine current environment
const getCurrentEnvironment = () => {
    // Check for explicit environment setting
    if (process.env.REACT_APP_ENV) {
        return process.env.REACT_APP_ENV;
    }

    // Check for environment-specific variables
    if (process.env.REACT_APP_API_URL) {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (apiUrl.includes('staging')) return 'staging';
        if (apiUrl.includes('datahub.n2nai.io')) return 'production';
    }

    // Default based on NODE_ENV
    return isDevelopment ? 'development' : 'production';
};

// Get URLs for current environment
const getUrls = (env = getCurrentEnvironment()) => {
    // Use environment variables if available, otherwise use defaults
    if (process.env.REACT_APP_API_URL || process.env.REACT_APP_JUPYTER_URL) {
        return {
            main: process.env.REACT_APP_MAIN_URL || domains[env].main,
            api: process.env.REACT_APP_API_URL || domains[env].api,
            jupyter: process.env.REACT_APP_JUPYTER_URL || domains[env].jupyter,
            airflow: process.env.REACT_APP_AIRFLOW_URL || domains[env].airflow,
            docs: process.env.REACT_APP_DOCS_URL || domains[env].docs,
            s3: process.env.REACT_APP_S3_URL || domains[env].s3
        };
    }

    return domains[env] || domains.development;
};

// Get specific service URL
const getServiceUrl = (service, env = getCurrentEnvironment()) => {
    const urls = getUrls(env);
    return urls[service] || urls.main;
};

// API Endpoints configuration
const apiEndpoints = {
    // Authentication
    auth: {
        login: '/auth/login',
        logout: '/auth/logout',
        refresh: '/auth/refresh',
        verify: '/auth/verify'
    },

    // Projects
    projects: {
        list: '/projects',
        create: '/projects',
        get: (projectName) => `/projects/${projectName}`,
        update: (projectName) => `/projects/${projectName}`,
        delete: (projectName) => `/projects/${projectName}`,
        // Files
        files: {
            upload: (projectName) => `/projects/${projectName}/files/upload`,
            list: (projectName) => `/projects/${projectName}/files`,
            download: (projectName, fileName) => `/projects/${projectName}/files/${fileName}`,
            delete: (projectName, fileName) => `/projects/${projectName}/files/${fileName}`
        },
        // MongoDB
        mongodb: {
            stats: (projectName) => `/projects/${projectName}/mongodb/stats`,
            collections: (projectName) => `/projects/${projectName}/mongodb/collections`
        },
        // Airflow
        airflow: {
            dags: (projectName) => `/projects/${projectName}/airflow/dags`,
            createDag: (projectName) => `/projects/${projectName}/airflow/dags`
        }
    },

    // JupyterHub
    jupyterhub: {
        generateToken: '/jupyterhub-api/auth/generate-token',
        validateToken: '/jupyterhub-api/auth/validate-token',
        validateCredentials: '/jupyterhub-api/auth/validate-credentials',
        getServer: (username) => `/jupyterhub-api/users/${username}/server`,
        startServer: (username) => `/jupyterhub-api/users/${username}/server`,
        stopServer: (username) => `/jupyterhub-api/users/${username}/server`,
        deleteUser: (username) => `/hub/user/delete?username=${username}`
    },

    // Billing
    billing: {
        usage: '/billing/usage',
        invoices: '/billing/invoices'
    },

    // Vector Database
    vector: {
        collections: '/vector/collections',
        search: '/vector/search',
        insert: '/vector/insert'
    }
};

// Helper function to build full API URL
const buildApiUrl = (endpoint, env = getCurrentEnvironment()) => {
    const baseUrl = getServiceUrl('api', env);
    return `${baseUrl}${endpoint}`;
};

// Helper to build JupyterHub URLs
const buildJupyterUrl = (path = '', env = getCurrentEnvironment()) => {
    const baseUrl = getServiceUrl('jupyter', env);
    return `${baseUrl}${path}`;
};

// Helper to build Airflow URLs
const buildAirflowUrl = (path = '', env = getCurrentEnvironment()) => {
    const baseUrl = getServiceUrl('airflow', env);
    return `${baseUrl}${path}`;
};

// Export current configuration
const config = {
    // Environment info
    environment: getCurrentEnvironment(),
    isDevelopment,
    isProduction,

    // URLs for current environment
    urls: getUrls(),

    // Service URLs
    API_URL: getServiceUrl('api'),
    JUPYTER_URL: getServiceUrl('jupyter'),
    AIRFLOW_URL: getServiceUrl('airflow'),
    DOCS_URL: getServiceUrl('docs'),
    S3_URL: getServiceUrl('s3'),
    MAIN_URL: getServiceUrl('main'),

    // Domain configuration
    domains,
    servicePorts,

    // Helper functions
    getUrls,
    getServiceUrl,
    buildApiUrl,
    buildJupyterUrl,
    buildAirflowUrl,

    // API endpoints
    apiEndpoints
};

// For backward compatibility
export const API_URL = config.API_URL;
export const JUPYTER_URL = config.JUPYTER_URL;
export const AIRFLOW_URL = config.AIRFLOW_URL;

export default config;
